import { servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";

/* ── Types ────────────────────────────────────────────────── */

export interface EmailTemplateType {
  TypeID: number;
  TypeName: string;
  ReqsPrimaryID: boolean;
  ReqsSecondaryID: boolean;
}

export interface EmailTemplate {
  TemplateID: number;
  TypeID: number;
  TemplateName: string;
  Subject: string;
  FromAddress: string;
  ReplyToAddress: string;
  Template?: string;
  OperatorID?: number;
  // Backend may return VB-style property names
  [key: string]: unknown;
}

export interface PartialEmailTemplate {
  TemplateID: number;
  TypeID: number;
  TemplateName: string;
  Template: string;
  Subject: string;
  FromAddress: string;
  ReplyToAddress: string;
  OperatorID?: number;
}

export interface ActiveEmailTemplate {
  TemplateID: number;
  TypeID: number;
  [key: string]: unknown;
}

export interface ValidEmailAddress {
  AddressID: number;
  Email: string;
}

export interface EmailTemplateInfo {
  TemplateTypes: EmailTemplateType[];
  Templates: EmailTemplate[];
  ActiveTemplates: ActiveEmailTemplate[];
  ValidEmailAddresses: ValidEmailAddress[];
}

/* ── API methods ──────────────────────────────────────────── */

export async function getEmailTemplateInfo(): Promise<EmailTemplateInfo> {
  const result = await batchedServicePost<EmailTemplateInfo>("WebGetEmailTemplateInfo", {});
  if (result.bError) {
    throw new Error(result.sMessage || "Failed loading email template info");
  }
  if (!result.oData) {
    throw new Error("No email template data returned");
  }
  const d = result.oData;
  d.TemplateTypes ??= [];
  d.Templates ??= [];
  d.ActiveTemplates ??= [];
  d.ValidEmailAddresses ??= [];
  return d;
}

// In-memory cache for read-only consumers (the Transaction operations modal's
// Comms tab). Templates rarely change within a session, so a single fetch per
// page load is plenty. Admin pages that *edit* templates must bypass this and
// call invalidateEmailTemplateInfoCache() after save — they already use the
// non-cached getEmailTemplateInfo() directly, so they always see fresh data.
let _emailTemplateInfoCache: EmailTemplateInfo | null = null;
let _emailTemplateInfoPromise: Promise<EmailTemplateInfo> | null = null;

export async function getEmailTemplateInfoCached(): Promise<EmailTemplateInfo> {
  if (_emailTemplateInfoCache) return _emailTemplateInfoCache;
  // Share an in-flight request between concurrent callers — opening the
  // Comms tab in two modals at once shouldn't fan out into two fetches.
  if (_emailTemplateInfoPromise) return _emailTemplateInfoPromise;
  _emailTemplateInfoPromise = (async () => {
    try {
      const d = await getEmailTemplateInfo();
      _emailTemplateInfoCache = d;
      return d;
    } finally {
      _emailTemplateInfoPromise = null;
    }
  })();
  return _emailTemplateInfoPromise;
}

export function invalidateEmailTemplateInfoCache(): void {
  _emailTemplateInfoCache = null;
  _emailTemplateInfoPromise = null;
}

export async function getEmailTemplate(templateId: number): Promise<PartialEmailTemplate> {
  const result = await batchedServicePost<PartialEmailTemplate>("WebGetEmailTemplate", { tempID: templateId });
  if (result.bError) {
    throw new Error(result.sMessage || "Failed loading email template");
  }
  if (!result.oData) {
    throw new Error("Template not found");
  }
  return result.oData;
}

export async function createEmailTemplate(args: {
  template: EmailTemplate;
  html: string;
  subject: string;
  fromAddress: string;
  replyAddress: string;
}): Promise<boolean> {
  const result = await servicePost<boolean>("WebCreateEmailTemplate", args);
  if (result.bError) {
    throw new Error(result.sMessage || "Failed creating email template");
  }
  return result.oData ?? false;
}

export async function saveEmailTemplate(template: PartialEmailTemplate): Promise<boolean> {
  const result = await servicePost<boolean>("WebSaveEmailTemplate", { template });
  if (result.bError) {
    throw new Error(result.sMessage || "Failed saving email template");
  }
  return result.oData ?? false;
}

export async function testEmailTemplate(args: {
  EmailTemplateID: number;
  Address: string;
  PrimaryID: number | null;
  SecondaryID: number | null;
}): Promise<boolean> {
  const result = await servicePost<boolean>("WebTestEmailTemplate", args);
  if (result.bError) {
    throw new Error(result.sMessage || "Failed sending test email");
  }
  return result.oData ?? false;
}

export async function setActiveEmailTemplate(
  emailTemplateID: number,
  operatorID: number,
): Promise<boolean> {
  const result = await servicePost<boolean>("WebSetActiveEmailTemplate", {
    EmailTemplateID: emailTemplateID,
    OperatorID: operatorID,
  });
  if (result.bError) {
    throw new Error(result.sMessage || "Failed setting active template");
  }
  return result.oData ?? false;
}

export async function getActiveEmailTemplateForType(
  operatorID: number,
  templateTypeID: number,
): Promise<string | null> {
  const result = await servicePost<string>("WebGetActiveEmailTemplateForType", {
    operatorID,
    templateTypeID,
  });
  if (result.bError) {
    return null;
  }
  return result.oData ?? null;
}
